import { Breakpoints } from '../breakpoints/breakpoints';
import moduleRegistry from '../module-registry/module-registry';

const config = {
    selectors: {
        root: '.location-map',
        map: '.location-map__map',
        box: '.location-map__box',
        mapMode: '.location-map__map-mode',
        locationMode: '.location-map__location-mode',
        zoomIn: '.location-map__zoom-in',
        zoomOut: '.location-map__zoom-out',
        overlay: '.location-map__overlay',
        overlayCloseBtn: '.location-map__overlay-close-btn',
        backdrop: '.location-map__backdrop',
    },
    dataAttributes: {
        url: 'url',
    },
    classNames: {
        mapModeActive: 'location-map__map-mode--active',
        locationModeActive: 'location-map__location-mode--active',
        backdropVisible: 'location-map__backdrop--visible',
    },
    worldZoom: 2.5,
    activeIconSvg: `
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24.5" cy="24.5" r="22.5" fill="white" fill-opacity="0.4"/>
      <circle cx="24" cy="24" r="16" fill="white" fill-opacity="0.5"/>
      <circle cx="24" cy="24" r="8" fill="white"/>
    </svg>
  `,
    iconSvg: `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="white"/>
    </svg>
  `,
};

class LocationMap {
    constructor($rootElement) {
        this._$rootElement = $rootElement;
        this._elements = null;
        this._bounds = null;
        this._map = null;
        this._locations = [];
        this._json = '';
        this._zoom = 0;
        this._mapOptions = null;
        this._currentMode = 'all';
        this._headquarterMarker = null;
        this._headquarter = null;
        this._markers = [];
        this._visibleMarkers = [];
        this._activeMarker = null;
        this._activeMarkerPrevIcon = null;
        this._overlayModifier = '';
        this._url = '';
        this._navigatorLocationLat = null;
        this._navigatorLocationLong = null;
        this._isInitialized = false;
    }

    init() {
        window.addEventListener('LocationMapInit', () => {
          this._afterInit();
        });
    }

    async _afterInit(attempt = 1) {
        if(this._isInitialized) return;

        if (typeof google !== 'object') {

          if(attempt > 10) {
            // console.log('google script not initialized, cant init map');
            return;
          } 

          const newAttempt = attempt + 1;
          
          setTimeout(() => {
            this._afterInit(newAttempt)
          }, 500 * attempt);
          
          return;
        }

        this._elements = this._getDomElements();
        this._url = this._$rootElement.data(config.dataAttributes.url);

        this._locations = await $.get(this._url);

        if (typeof this._locations !== 'object') {
            // console.log('couldnt fetch locations');
            return;
        }

        const processedLocations = [];

        this._locations.forEach(location => {
          if(!(location.latitude === 0.0 && location.longitude === 0.0)) {
            processedLocations.push(location);
          }
        });

        this._locations = processedLocations;

        if(this._locations.length === 0) {
          // console.log('no locations (or all have lat 0.0 and lon 0.0)');
          return;
        }

        this._locations.forEach((location) => {
            location.latlng = new google.maps.LatLng(location.latitude, location.longitude);
        });

        this._json = this._getWorldJson();

        this._zoom = config.worldZoom;
        this._addEventListeners();
        this._initMenuScroll();

        this._isInitialized = true;
    }

    _getDomElements() {
        return {
            $map: this._$rootElement.find(config.selectors.map),
            $box: this._$rootElement.find(config.selectors.box),
            $locationMode: this._$rootElement.find(config.selectors.locationMode),
            $yearControlBtn: this._$rootElement.find(config.selectors.yearControlBtn),
            $zoomIn: this._$rootElement.find(config.selectors.zoomIn),
            $zoomOut: this._$rootElement.find(config.selectors.zoomOut),
            $overlay: this._$rootElement.find(config.selectors.overlay),
            $overlayCloseBtn: this._$rootElement.find(config.selectors.overlayCloseBtn),
            $backdrop: this._$rootElement.find(config.selectors.backdrop),
        };
    }


    async _addEventListeners() {
        const _this = this;

        this._elements.$locationMode.on('click', async function () {
            _this._elements.$locationMode.removeClass(config.classNames.locationModeActive);
            $(this).addClass(config.classNames.locationModeActive);

            if(_this._elements.$backdrop.hasClass(config.classNames.backdropVisible)) {
              _this._hideBackdrop();
              _this._hideOverlay();
            }

            _this._currentMode = $(this).data('location-mode-type');

            let locations;

            if (_this._currentMode == 'near') {
                _this._map.setOptions({draggableCursor: 'progress'});
                const position = await _this._setUserPosition();
                _this._navigatorLocationLong = position.coords.longitude;
                _this._navigatorLocationLat = position.coords.latitude;
                locations = _this._getLocationsByType(_this._currentMode);
            } else {
                locations = _this._getLocationsByType(_this._currentMode);
            }

            _this._locations = locations;
            // _this._initMap();

            const newVisibleMarkers = [];

            _this._markers.forEach((marker) => {
                if (_this._currentMode === marker.type || _this._currentMode === '' || _this._currentMode === 'near') {
                    marker.setMap(_this._map);
                    newVisibleMarkers.push(marker);
                } else {
                    marker.setMap(null);
                }
            });

            _this._visibleMarkers = newVisibleMarkers;

            if (_this._currentMode === 'headquarter') {
                const center = _this._calcCenterOfVisibleMarkers();
                _this._elements.$overlay.removeClass(_this._overlayModifier);
                _this._activeMarkerPrevIcon = _this._headquarterMarker.getIcon();
                _this._headquarterMarker.setIcon(`data:image/svg+xml;charset=UTF-8;base64,${btoa(_this._createActiveIconSvg(_this._getColorByType(_this._headquarter.type)))}`);
                _this._activeMarker = _this._headquarterMarker;
                _this._overlayModifier = `location-map__overlay--${_this._activeMarker.type}`;
                _this._focusPoint(center[1], center[0]);

                google.maps.event.addListenerOnce(_this._map, 'idle', () => {
                    setTimeout(() => {
                        const offsetTop = 0;

                        // Dont use Durag names
                        // const title = this.address[j].name;
                        const { title } = _this._headquarter;
                        const { description } = _this._headquarter;
                        const { country } = _this._headquarter;
                        const { link } = _this._headquarter;
                        const iconColor = _this._getColorByType(_this._headquarter.type);
                        const content = _this._createOverlayHtml(title, description, country, '#fff', link);

                        _this._elements.$overlay.html(content);

                        _this._showOverlay(iconColor, offsetTop);
                        _this._showBackdrop();
                        _this._initOverlayListeners();
                    }, 100);
                });
            } else if (_this._currentMode == 'near') {
                _this._zoom = 7;
                _this._map.setZoom(7);
                _this._map.panTo(new google.maps.LatLng(_this._navigatorLocationLat, _this._navigatorLocationLong));
                _this._map.setOptions({draggableCursor: ''});
            } else {
                const center = _this._calcCenterOfVisibleMarkers();
                _this._zoom = 2.5;
                _this._map.setZoom(2.5);
                _this._map.panTo(new google.maps.LatLng(center[1], center[0]));
            }
        });

        this._elements.$zoomIn.on('click', () => {
            this._zoom++;
            this._map.setZoom(this._zoom);
        });

        this._elements.$backdrop.on('click', () => {
          if(this._elements.$backdrop.hasClass(config.classNames.backdropVisible)) {
            _this._hideBackdrop();
            _this._hideOverlay(); 
          }
        });
       
        this._elements.$zoomOut.on('click', () => {
            this._zoom--;

            if (this._zoom < config.worldZoom) {
                this._zoom = config.worldZoom;
            }

            this._map.setZoom(this._zoom);
        });

        window.addEventListener('load', this._initMap());
    }

    _getLocationsByType(type) {
        const locations = [];

        this._locations.forEach((e) => {
            if (type === '' || e.type === type || type === 'near') {
                locations.push(e);
            }
        });

        return locations;
    }

    _getColorByType(type) {
        let iconColor = '';

        switch (type) {
            case 'manufactoring': iconColor = '#E6883B'; break;
            case 'sales': iconColor = '#00A854'; break;
            case 'training': iconColor = '#C4C746'; break;
            case 'headquarter': iconColor = '#8CB4F3'; break;
        }

        return iconColor;
    }

    _createOverlayHtml(title, description, country, closeColor, link) {
      return `
        <div class="location-map__overlay-close-btn" data-hover="true" style="color: ${closeColor};"><span class="location-map__overlay-close-btn-icon icon-close"></span></div>
        <div class="location-map__overlay-inner">
            <${link ? `a href="${link}"` : 'span'} class="location-map__overlay-headline">
              ${title}
              ${link ? `
                <span class="location-map__overlay-icon icon-StateDark16">
                  <span class="path1"></span>
                  <span class="path2"></span>
                </span>` 
              : ''}              
            </${link ? 'a' : 'span'}>
            <div class="location-map__overlay-subline">${country}</div>
            <div class="location-map__overlay-description">${description}</div>
        </div>
      `;
    }

    _initMenuScroll() {
        const slider = document.querySelector('.location-map__location-modes');
        let isDown = false;
        let startX;
        let scrollLeft;

        slider.addEventListener('mousedown', (e) => {
            isDown = true;
            slider.classList.add('active');
            startX = e.pageX - slider.offsetLeft;
            scrollLeft = slider.scrollLeft;
        });
        slider.addEventListener('mouseleave', () => {
            isDown = false;
            slider.classList.remove('active');
        });
        slider.addEventListener('mouseup', () => {
            isDown = false;
            slider.classList.remove('active');
        });
        slider.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - slider.offsetLeft;
            const walk = (x - startX) * 3;
            slider.scrollLeft = scrollLeft - walk;
        });

        const styleElem = document.head.appendChild(document.createElement('style'));
        styleElem.innerHTML = `.location-map__location-modes:after {width: ${slider.scrollWidth}px;}`;
    }

    _showOverlay(iconColor, offsetTop) {
        this._elements.$overlay.css('background-color', iconColor);
        this._elements.$overlay.css('height', 'auto');
        this._elements.$overlay.css('opacity', 1);
        this._elements.$overlay.css('pointer-events', 'all');

        if (Breakpoints.isBreakpoint('sm') || Breakpoints.isBreakpoint('xs')) {
            this._elements.$overlay.css('left', '20px');
            this._elements.$overlay.css('right', '20px');
            this._elements.$overlay.css('top', 'auto');
            this._elements.$overlay.css('bottom', '20px');
            this._elements.$overlay.css('width', 'auto');
            this._elements.$overlay.css('transform', 'none');
        } else {
            const boxBottom = this._elements.$box[0].offsetTop + this._elements.$box[0].clientHeight;
            const overlayHeight = this._elements.$overlay[0].clientHeight;
            const calculatedOverlayTop = offsetTop - (overlayHeight / 2);
            const newOverlayTop = calculatedOverlayTop > boxBottom ? calculatedOverlayTop : boxBottom + 10;

            // this._elements.$overlay.css('top', newOverlayTop);
            this._elements.$overlay.css('top', '50%');
            this._elements.$overlay.css('left', '50%');
            // this._elements.$overlay.css('right', 'calc(50% + 20px)');
            this._elements.$overlay.css('bottom', 'auto');
            // this._elements.$overlay.css('transform', 'none');
            this._elements.$overlay.css('width', '300px');
            this._elements.$overlay.css('transform', 'translate(calc(-50% - 200px), calc(-50% + 150px))');
        }
    }

    _showBackdrop() {
        this._elements.$backdrop.addClass(config.classNames.backdropVisible);
    }

    _hideBackdrop() {
        this._elements.$backdrop.removeClass(config.classNames.backdropVisible);
    }

    _initOverlayListeners() {
        this._elements.$overlay.find(config.selectors.overlayCloseBtn).on('click', () => {
            this._hideBackdrop();
            this._hideOverlay();
        });
    }

    _createIconSvg(iconColor) {
        return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="12" fill="${iconColor}" fill-opacity="0"/>  
        <circle cx="12" cy="12" r="4" fill="${iconColor}"/>
        </svg>
      `;
    }

    _createActiveIconSvg(iconColor) {
        return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="12" fill="${iconColor}" fill-opacity="0.4" />
        <circle cx="12" cy="12" r="8" fill="${iconColor}" fill-opacity="0.5" />
        <circle cx="12" cy="12" r="4" fill="${iconColor}"/>
        </svg>
      `;
    }

    _createMarker(iconUrl, location) {
        return new google.maps.Marker({
            icon: iconUrl,
            position: location.latlng,
            map: this._map,
            unreplacedTitle: location.title,
            title: location.title.replaceAll('&nbsp;', ' ').replaceAll('&amp;', '&').replaceAll('&auml;', 'ä').replaceAll('&uuml;', 'ü').replaceAll('&ouml;', 'ö'),
            description: location.description,
            address: location.address,
            type: location.type,
            country: location.country,
            link: location.link,
            
        });
    }

    _hideMarkers() {

    }

    _focusPoint(lat, lon) {
        this._zoom = 6;
        this._map.setZoom(6);
        this._map.panTo(new google.maps.LatLng(lat, lon));
    }

    _createAndReturnMarkers(locations) {
        const markers = [];

        for (let i = 0; i < locations.length; i++) {
            const iconColor = this._getColorByType(locations[i].type);
            const iconSvg = this._createIconSvg(iconColor);
            const iconUrl = `data:image/svg+xml;charset=UTF-8;base64,${btoa(iconSvg)}`;

            const marker = this._createMarker(iconUrl, locations[i]);

            markers.push(marker);

            if (locations[i].type === 'headquarter') {
                this._headquarterMarker = marker;
                this._headquarter = locations[i];
            }

            const _this = this;

            google.maps.event.addListener(marker, 'click', function (e) {
                if (!e) {
                    _this._focusPoint(_this._headquarter.latlng.lat, _this._headquarter.latlng.lon);
                } else {
                    _this._focusPoint(e.latLng.lat(), e.latLng.lng());

                    _this._elements.$overlay.removeClass(_this._overlayModifier);
                    _this._activeMarker = marker;
                    _this._overlayModifier = `location-map__overlay--${_this._activeMarker.type}`;
                    _this._elements.$overlay.addClass(_this._overlayModifier);
                    _this._activeMarkerPrevIcon = marker.getIcon();
                    marker.setIcon(`data:image/svg+xml;charset=UTF-8;base64,${btoa(_this._createActiveIconSvg(iconColor))}`);

                    google.maps.event.addListenerOnce(_this._map, 'idle', () => {
                        setTimeout(() => {
                            let offsetTop = e.domEvent.y;

                            if (e.domEvent.path && e.domEvent.path[3]) {
                                const $markerWrapper = e.domEvent.path[3];
                                const $marker = e.domEvent.path[1];

                                offsetTop = $markerWrapper.offsetTop + $marker.offsetTop;
                            }

                            // Dont use Durag names
                            // const title = this.address[j].name;
                            const { title } = this;
                            const { description } = this;
                            const { country } = this;
                            const { link } = this;
                            const {unreplacedTitle} = this;
                            const color = this.type === 'manufactoring' ? '#000' : '#fff';

                            const content = _this._createOverlayHtml(unreplacedTitle, description, country, color, link);

                            _this._elements.$overlay.html(content);

                            _this._showOverlay(iconColor, offsetTop);
                            _this._showBackdrop();
                            _this._initOverlayListeners();
                        }, 100);
                    });
                }
            });
        }

        return markers;
    }

    _setMarkers(locations) {
        this._bounds = new google.maps.LatLngBounds();
        this._markers = this._createAndReturnMarkers(locations);
        this._visibleMarkers = this._markers;
        this._markers.forEach((marker) => {
            this._bounds.extend(marker.position);
        });
    }

    _hideOverlay() {
    // this._elements.$overlay.css('top', 0);
        this._elements.$overlay.css('opacity', 0);
        this._elements.$overlay.css('pointer-events', 'none');
        this._activeMarker.setIcon(this._activeMarkerPrevIcon);
        setTimeout(() => {
            // this._elements.$overlay.css('height', '0');
        }, 500);
    }

    _initMap() {
        const maps = this._elements.$map;

        const styledMapType = new google.maps.StyledMapType(
            this._json,
            { name: maps[0].dataset.type },
        );

        const centerOfLocations = this._calcCenterOfLocations(this._locations);

        this._mapOptions = {
            draggable: true,
            scrollwheel: 'CTRL',
            zoomControl: false,
            minZoom: config.worldZoom,
            maxZoom: config.worldZoom + 12,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
        };

        this._initOptions = {
            center: new google.maps.LatLng(centerOfLocations[1], centerOfLocations[0]),
            zoom: this._zoom,
            zoomControl: false,
            minZoom: config.worldZoom,
            maxZoom: config.worldZoom + 12,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            mapTypeControlOptions: {
                mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain', maps[0].dataset.type],
            },
        };

        this._map = new google.maps.Map(maps[0], this._initOptions);

        this._bounds = new google.maps.LatLngBounds();
        this._setMarkers(this._locations);

        this._map.mapTypes.set(maps[0].dataset.type, styledMapType);
        this._map.setMapTypeId(maps[0].dataset.type);

        window.onresize = () => {
            this._map.fitBounds(this._bounds);
        };

        if (this._currentMode === 'headquarter') {
            setTimeout(() => {
                google.maps.event.trigger(this._headquarterMarker, 'click');
            }, 500);
        }
    }

    // helper functions

    _setUserPosition() {
        return new Promise((res, rej) => {
            navigator.geolocation.getCurrentPosition(res, rej);
        });
    }

    _calcCenterOfLocations(locations) {
        let sumX = 0;
        let sumY = 0;
        let sumZ = 0;

        locations.forEach((location) => {
            const lat = this._degr2rad(location.latlng.lat());
            const lng = this._degr2rad(location.latlng.lng());
            // sum of cartesian coordinates
            sumX += Math.cos(lat) * Math.cos(lng);
            sumY += Math.cos(lat) * Math.sin(lng);
            sumZ += Math.sin(lat);
        });
        
        const avgX = sumX / locations.length;
        const avgY = sumY / locations.length;
        const avgZ = sumZ / locations.length;

        const lng = Math.atan2(avgY, avgX);
        const hyp = Math.sqrt(avgX * avgX + avgY * avgY);
        const lat = Math.atan2(avgZ, hyp);

        return ([this._rad2degr(lng), this._rad2degr(lat)]);
    }

    _calcCenterOfVisibleMarkers() {
        let sumX = 0;
        let sumY = 0;
        let sumZ = 0;

        this._visibleMarkers.forEach((marker) => {
            const lat = this._degr2rad(marker.position.lat());
            const lng = this._degr2rad(marker.position.lng());
            // sum of cartesian coordinates
            sumX += Math.cos(lat) * Math.cos(lng);
            sumY += Math.cos(lat) * Math.sin(lng);
            sumZ += Math.sin(lat);
        });

        const avgX = sumX / this._visibleMarkers.length;
        const avgY = sumY / this._visibleMarkers.length;
        const avgZ = sumZ / this._visibleMarkers.length;

        const lng = Math.atan2(avgY, avgX);
        const hyp = Math.sqrt(avgX * avgX + avgY * avgY);
        const lat = Math.atan2(avgZ, hyp);

        return ([this._rad2degr(lng), this._rad2degr(lat)]);
    }

    _rad2degr(rad) {
        return rad * 180 / Math.PI;
    }

    _degr2rad(degr) {
        return degr * Math.PI / 180;
    }

    // configuration for map styling
    _getWorldJson() {
        return [
          {
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#4c5b7b"
              }
            ]
          },
          {
            "elementType": "labels",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "administrative",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "administrative",
            "elementType": "labels",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "administrative.country",
            "stylers": [
              {
                "visibility": "on"
              }
            ]
          },
          {
            "featureType": "administrative.country",
            "elementType": "geometry.stroke",
            "stylers": [
              {
                "color": "#074089"
              },
              {
                "weight": 1.5
              }
            ]
          },
          {
            "featureType": "administrative.country",
            "elementType": "labels",
            "stylers": [
              {
                "color": "#242b3e"
              },
              {
                "saturation": -5
              },
              {
                "lightness": 5
              }
            ]
          },
          {
            "featureType": "administrative.country",
            "elementType": "labels.text",
            "stylers": [
              {
                "color": "#5a5c63"
              },
              {
                "visibility": "off"
              },
              {
                "weight": 0.5
              }
            ]
          },
          {
            "featureType": "administrative.country",
            "elementType": "labels.text.stroke",
            "stylers": [
              {
                "color": "#505258"
              },
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "administrative.province",
            "elementType": "geometry.stroke",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "landscape",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#4c5b7b"
              }
            ]
          },
          {
            "featureType": "landscape",
            "elementType": "geometry.fill",
            "stylers": [
              {
                "color": "#05326c"
              }
            ]
          },
          {
            "featureType": "landscape.man_made",
            "elementType": "labels.text",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "poi",
            "elementType": "labels",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "road",
            "elementType": "labels",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "transit",
            "elementType": "geometry",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "transit",
            "elementType": "labels",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "transit.line",
            "elementType": "geometry.fill",
            "stylers": [
              {
                "color": "#283d6a"
              }
            ]
          },
          {
            "featureType": "transit.station",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#3a4762"
              }
            ]
          },
          {
            "featureType": "transit.station.airport",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          },
          {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#074089"
              },
              {
                "visibility": "on"
              }
            ]
          },
          {
            "featureType": "water",
            "elementType": "labels",
            "stylers": [
              {
                "visibility": "off"
              }
            ]
          }
        ];
         
    }
}

moduleRegistry.registerModule('LocationMap', LocationMap);
moduleRegistry.autoApplyModule('LocationMap', config.selectors.root);